import type { EquipmentStatus } from '@/types/equipment';

// ============================================================================
// EquipmentStatusPill
// ----------------------------------------------------------------------------
// Single source of truth for status colors and labels. 11 states per AAMI
// ST79. Color logic loosely follows the cycle:
//
//   green     — ready states (available, sterile-ready)
//   cyan      — actively in use
//   pink/red  — soiled / contaminated states (urgent attention)
//   purple    — in-progress reprocessing (decon, sterilization)
//   teal      — clean intermediate state (clean-and-inspected)
//   amber     — needs attention (maintenance, awaiting-decontamination)
//   red       — out-of-service (problem)
//   dim       — retired (terminal)
//
// Labels are intentionally short (1-3 words) to fit the table cell.
// ============================================================================

interface Props {
    status: EquipmentStatus;
    size?: 'sm' | 'md';
}

const STATUS_META: Record<
    EquipmentStatus,
    { label: string; bg: string; text: string; border: string }
> = {
    'available': {
        label: 'Available',
        bg: 'bg-pt-green/10',
        text: 'text-pt-green',
        border: 'border-pt-green/30'
    },
    'in-use': {
        label: 'In Use',
        bg: 'bg-pt-cyan/10',
        text: 'text-pt-cyan',
        border: 'border-pt-cyan/30'
    },
    'soiled': {
        label: 'Soiled',
        bg: 'bg-pt-pink/10',
        text: 'text-pt-pink',
        border: 'border-pt-pink/30'
    },
    'awaiting-decontamination': {
        label: 'Awaiting Decon',
        bg: 'bg-pt-amber/10',
        text: 'text-pt-amber',
        border: 'border-pt-amber/30'
    },
    'in-decontamination': {
        label: 'In Decon',
        bg: 'bg-pt-purple/10',
        text: 'text-pt-purple',
        border: 'border-pt-purple/30'
    },
    'clean-and-inspected': {
        label: 'Clean & Inspected',
        bg: 'bg-pt-cyan/10',
        text: 'text-pt-cyan',
        border: 'border-pt-cyan/30'
    },
    'in-sterilization': {
        label: 'Sterilizing',
        bg: 'bg-pt-purple/10',
        text: 'text-pt-purple',
        border: 'border-pt-purple/30'
    },
    'sterile-ready': {
        label: 'Sterile / Ready',
        bg: 'bg-pt-green/10',
        text: 'text-pt-green',
        border: 'border-pt-green/30'
    },
    'maintenance': {
        label: 'Maintenance',
        bg: 'bg-pt-amber/10',
        text: 'text-pt-amber',
        border: 'border-pt-amber/30'
    },
    'out-of-service': {
        label: 'Out of Service',
        bg: 'bg-pt-red/10',
        text: 'text-pt-red',
        border: 'border-pt-red/30'
    },
    'retired': {
        label: 'Retired',
        bg: 'bg-pt-text-dim/10',
        text: 'text-pt-text-dim',
        border: 'border-pt-text-dim/30'
    }
};

export default function EquipmentStatusPill({ status, size = 'md' }: Props) {
    const meta = STATUS_META[status] ?? STATUS_META['available'];
    const sizing =
        size === 'sm'
            ? 'px-2 py-0.5 text-[10px]'
            : 'px-2.5 py-1 text-xs';
    return (
        <span
            className={`inline-flex items-center gap-1.5 ${sizing} font-medium uppercase tracking-wide rounded border ${meta.bg} ${meta.text} ${meta.border}`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${meta.text.replace('text-', 'bg-')}`} />
            {meta.label}
        </span>
    );
}
